import React, { useState, useEffect } from 'react';
import { ArrowLeft, Building2, Plus, Trash2, Edit2, Upload, FolderOpen } from 'lucide-react';
import ComponentImport from '../components/ComponentImport';
import FloorPlanEditor from '../components/FloorPlanEditor';

const AdminFloorPlans = ({ facilityId, facilityName, onBack }) => {
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [floorplans, setFloorplans] = useState([]);
  const [editingFloorplan, setEditingFloorplan] = useState(null);
  const [newBuildingName, setNewBuildingName] = useState('');
  const [newFloorplanName, setNewFloorplanName] = useState('');
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [showAddFloorplan, setShowAddFloorplan] = useState(false);

  const fetchBuildings = async () => {
    const res = await fetch(`/api/facilities/${facilityId}/buildings`);
    const data = await res.json();
    setBuildings(data);
  };

  const fetchFloorplans = async (buildingId) => {
    const res = await fetch(`/api/buildings/${buildingId}/floorplans`);
    const data = await res.json();
    setFloorplans(data);
  };

  useEffect(() => {
    fetchBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  useEffect(() => {
    if (selectedBuilding) {
      fetchFloorplans(selectedBuilding);
    }
  }, [selectedBuilding]);

  const handleAddBuilding = async () => {
    if (!newBuildingName.trim()) return;

    await fetch(`/api/facilities/${facilityId}/buildings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newBuildingName, order: buildings.length })
    });

    setNewBuildingName('');
    setShowAddBuilding(false);
    fetchBuildings();
  };

  const handleDeleteBuilding = async (buildingId) => {
    if (!window.confirm('Gebäude und alle Pläne löschen?')) return;

    await fetch(`/api/buildings/${buildingId}`, { method: 'DELETE' });
    setSelectedBuilding(null);
    setFloorplans([]);
    fetchBuildings();
  };

  const handleAddFloorplan = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedBuilding) {
      console.error('No file or building selected', { file, selectedBuilding });
      return;
    }

    const url = `/api/buildings/${selectedBuilding}/floorplans`;
    console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('URL:', url, 'Building ID:', selectedBuilding, 'Type:', typeof selectedBuilding);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', newFloorplanName || file.name.replace(/\.[^/.]+$/, ''));
    formData.append('order', floorplans.length);

    try {
      const res = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        console.error('Upload failed:', error);
        alert('Upload fehlgeschlagen: ' + (error.message || 'Unbekannter Fehler'));
        return;
      }

      setNewFloorplanName('');
      setShowAddFloorplan(false);
      fetchFloorplans(selectedBuilding);
    } catch (error) {
      console.error('Upload error:', error.name, error.message, error);
      alert('Verbindungsfehler beim Upload: ' + error.message);
    }
  };

  const handleDeleteFloorplan = async (floorplanId) => {
    if (!window.confirm('Plan löschen?')) return;

    await fetch(`/api/floorplans/${floorplanId}`, { method: 'DELETE' });
    fetchFloorplans(selectedBuilding);
  };

  return (
    <div className="min-h-screen bg-[#F2E2D2] p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-[#e8d5c7] text-[#433225]"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#433225]">Lageplan-Verwaltung</h1>
          <p className="text-sm text-[#433225]/70">Anlage: {facilityName || facilityId}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Buildings list */}
        <div className="bg-[#f7e6d8] rounded-xl p-6 shadow-lg">
          <h2 className="text-lg font-semibold text-[#433225] mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Gebäude
          </h2>

          <div className="space-y-2 mb-4">
            {buildings.map(building => (
              <div
                key={building._id}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedBuilding === building._id
                    ? 'bg-[#e89f71] text-white'
                    : 'bg-white hover:bg-[#e8d5c7] text-[#433225]'
                }`}
                onClick={() => setSelectedBuilding(building._id)}
              >
                <span className="font-medium">{building.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteBuilding(building._id);
                  }}
                  className="p-1 hover:bg-red-500/20 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {showAddBuilding ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newBuildingName}
                onChange={(e) => setNewBuildingName(e.target.value)}
                placeholder="Gebäudename"
                className="flex-1 px-3 py-2 rounded-lg border border-[#e8d5c7] focus:ring-2 focus:ring-[#e89f71]"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAddBuilding()}
              />
              <button
                onClick={handleAddBuilding}
                className="px-4 py-2 bg-[#e89f71] text-white rounded-lg hover:bg-[#d88f61]"
              >
                OK
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddBuilding(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-[#e8d5c7] rounded-lg hover:border-[#e89f71] text-[#433225]"
            >
              <Plus className="w-4 h-4" />
              Gebäude hinzufügen
            </button>
          )}
        </div>

        {/* Floorplans list */}
        <div className="bg-[#f7e6d8] rounded-xl p-6 shadow-lg">
          <h2 className="text-lg font-semibold text-[#433225] mb-4 flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Pläne
            {selectedBuilding && (
              <span className="text-sm font-normal text-[#433225]/70">
                ({buildings.find(b => b._id === selectedBuilding)?.name})
              </span>
            )}
          </h2>

          {selectedBuilding ? (
            <>
              <div className="space-y-2 mb-4">
                {floorplans.map(fp => (
                  <div
                    key={fp._id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg text-[#433225]"
                  >
                    <span className="font-medium">{fp.name}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingFloorplan(fp._id)}
                        className="p-1 hover:bg-[#e8d5c7] rounded"
                        title="Komponenten platzieren"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFloorplan(fp._id)}
                        className="p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {showAddFloorplan ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newFloorplanName}
                    onChange={(e) => setNewFloorplanName(e.target.value)}
                    placeholder="Planname (optional)"
                    className="w-full px-3 py-2 rounded-lg border border-[#e8d5c7] focus:ring-2 focus:ring-[#e89f71]"
                  />
                  <label className="flex items-center justify-center gap-2 px-4 py-2 bg-[#e89f71] text-white rounded-lg hover:bg-[#d88f61] cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Bild/PDF auswählen
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleAddFloorplan}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddFloorplan(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-[#e8d5c7] rounded-lg hover:border-[#e89f71] text-[#433225]"
                >
                  <Plus className="w-4 h-4" />
                  Plan hochladen
                </button>
              )}
            </>
          ) : (
            <p className="text-center text-[#433225]/50 py-8">
              Wähle zuerst ein Gebäude
            </p>
          )}
        </div>

        {/* Component import */}
        <div>
          <ComponentImport
            facilityId={facilityId}
            onImportComplete={() => {}}
          />
        </div>
      </div>

      {/* Editor modal */}
      {editingFloorplan && (
        <FloorPlanEditor
          facilityId={facilityId}
          floorplanId={editingFloorplan}
          onClose={() => setEditingFloorplan(null)}
        />
      )}
    </div>
  );
};

export default AdminFloorPlans;
